import beerIcon from "../assets/page_logo.png";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { authApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";

function CreatePassword() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if user has access_token and temp_password
  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");
    const tempPassword = localStorage.getItem("temp_password");

    if (!accessToken || !tempPassword) {
      // No signup session (e.g. user backed out) — redirect silently, no blocking popup.
      // replace: don't leave a dead /create-password entry the back button lands on again.
      navigate("/admin-signup", { replace: true });
    }
  }, [navigate]);

  // Password validation criteria
  const criteria = {
    upper: /[A-Z]/.test(newPassword),
    number: /\d/.test(newPassword),
    lower: /[a-z]/.test(newPassword),
    special: /[!@#^*_?{}-]/.test(newPassword),
    length: newPassword.length >= 8,
  };

  const allSatisfied = Object.values(criteria).every(Boolean);
  const passwordsMatch =
    confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSave = allSatisfied && passwordsMatch;

  const rules = [
    {
      label: "At least one uppercase letter",
      satisfied: criteria.upper,
    },
    {
      label: "At least one numeral (0-9)",
      satisfied: criteria.number,
    },
    {
      label: "At least one lowercase letter",
      satisfied: criteria.lower,
    },
    {
      label: "At least one special symbol (!@#^*_?{}-)",
      satisfied: criteria.special,
    },
    {
      label: "Minimum of 8 characters",
      satisfied: criteria.length,
    },
  ];

  const handleSavePassword = async () => {
    if (!canSave) {
      toast.error("Please meet all password requirements.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const accessToken = localStorage.getItem("access_token");
    const tempPassword = localStorage.getItem("temp_password");
    const userEmail = localStorage.getItem("userEmail");

    if (!accessToken || !tempPassword || !userEmail) {
      toast.error("Session expired. Please start the signup process again.", {
        position: "top-right",
        autoClose: 3000,
      });
      navigate("/admin-signup");
      return;
    }

    try {
      // Step 1: Change password. authClient attaches the bearer and refreshes
      // it first if the session from OTP confirmation has aged out.
      await authApi.changePasswordForSignup({
        previous_password: tempPassword,
        new_password: newPassword,
        proposed_password: newPassword,
      });

      // Step 3: Login with new password using auth context
      try {
        const loginResult = await login({
          email: userEmail,
          password: newPassword,
        });

        // The password has already been changed by this point, so temp_password is
        // spent either way. Leaving it behind on the failure path used to let the
        // mount guard wave the partner back in with a credential that could never
        // work again (#29, AC-PARTNER-ERR7).
        localStorage.removeItem("temp_password");
        localStorage.removeItem("userEmail");

        if (loginResult.success) {
          toast.success("Password changed successfully! Redirecting...", {
            position: "top-right",
            autoClose: 2000,
          });
        } else {
          // Don't bail to /admin-login — that skips the agreement and profile steps
          // and strands the partner with an account and no business. The access token
          // from OTP confirmation is still valid (changing a password does not sign
          // Cognito sessions out), so onboarding can continue on it.
          console.warn("[Create Password] Post-change login failed; continuing onboarding");
          toast.warn("Password changed. Continuing setup…", {
            position: "top-right",
            autoClose: 3000,
          });
        }

        setTimeout(() => {
          navigate("/partner-agreement");
        }, 500);
      } catch (loginError) {
        console.error("🚨 [Create Password] Login error:", loginError);
        localStorage.removeItem("temp_password");
        localStorage.removeItem("userEmail");
        toast.warn("Password changed. Continuing setup…", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => {
          navigate("/partner-agreement");
        }, 500);
      }
    } catch (err) {
      console.error("🚨 [Create Password] Error:", err);
      // authClient throws {error, message, status}. This used to read only
      // err.message on an object that carried just .error, so every failure
      // here showed the generic fallback and the real reason never surfaced.
      if (err?.isAuthError) return; // already being signed out
      toast.error(
        err?.error || err?.message || "Failed to change password. Please try again.",
        {
          position: "top-right",
          autoClose: 4000,
        },
      );
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#F9F4EA",
        width: "100%",
        // minHeight, not height, and no `overflow: hidden`. Together those two made anything
        // taller than the viewport unreachable — and because this row centres its content,
        // what did not fit spilled off BOTH edges with no way to scroll to either. On iOS the
        // collapsing URL bar makes 100vh taller than the visible area as well. AC-MOBILE-F2.
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          // The 400px logo and the card beside it total ~950px. Wrapping puts them on
          // separate centred lines below that width, which is the stacked phone layout —
          // no breakpoint, no state, one property. AC-MOBILE-F2.
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "1200px",
          gap: "clamp(24px, 6vw, 80px)",
          padding: "20px",
          boxSizing: "border-box",
        }}
      >
        {/* Left: MyHoom + Beer Icon */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <img
            src={beerIcon}
            alt="beer icon"
            style={{ width: "min(400px, 60vw)", height: "auto", cursor: "pointer" }}
            onClick={() => navigate("/admin-signup")}
          />
        </div>

        {/* Right: Form */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "min(472px, 100%)",
              // minHeight, not height: the card grows when its contents wrap on a
              // narrow screen instead of spilling out of a fixed box. AC-MOBILE-F2.
              minHeight: "734px",
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              padding: "30px",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            <h3
              style={{
                fontSize: "32px",
                fontWeight: "bold",
                color: "#1E344D",
                textAlign: "left",
                marginBottom: "40px",
              }}
            >
              Create New Password
            </h3>

            {/* New Password Input */}
            <div
              style={{
                position: "relative",
                width: "100%",
                marginBottom: "24px",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  marginBottom: "5px",
                  display: "block",
                }}
              >
                New Password<span style={{ color: "red" }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  style={{
                    width: "100%",
                    height: "56px",
                    padding: "12px 50px 12px 12px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    boxSizing: "border-box",
                    fontSize: "16px",
                  }}
                />
                {/* Eye icon for password visibility */}
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  {showPassword ? (
                    <img
                      src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-fill.svg"
                      alt="Hide"
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : (
                    <img
                      src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-slash-fill.svg"
                      alt="Show"
                      style={{ width: "20px", height: "20px" }}
                    />
                  )}
                </span>
                {/* Check icon when all criteria are satisfied */}
                {allSatisfied && (
                  <span
                    style={{
                      position: "absolute",
                      right: "40px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <img
                      src={require("../assets/check.png")}
                      alt="valid"
                      style={{ width: "16px", height: "16px" }}
                    />
                  </span>
                )}
              </div>
            </div>

            {/* Password Rules */}
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                marginBottom: "24px",
              }}
            >
              {rules.map((rule, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "8px",
                    color: rule.satisfied ? "#1E344D" : "rgba(30, 52, 77, 0.3)",
                    fontSize: "14px",
                    transition: "color 0.3s ease",
                  }}
                >
                  <img
                    src={require("../assets/check.png")}
                    alt="check"
                    style={{
                      width: "14px",
                      height: "14px",
                      marginRight: "8px",
                      opacity: rule.satisfied ? 1 : 0.3,
                      transition: "opacity 0.3s ease",
                    }}
                  />
                  {rule.label}
                </li>
              ))}
            </ul>

            {/* Confirm Password Input */}
            <div style={{ marginBottom: "40px" }}>
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  marginBottom: "5px",
                  display: "block",
                }}
              >
                Confirm New Password<span style={{ color: "red" }}>*</span>
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  style={{
                    width: "100%",
                    height: "56px",
                    padding: "12px 50px 12px 12px",
                    borderRadius: "4px",
                    border:
                      passwordsMatch && confirmPassword.length > 0
                        ? "1px solid green"
                        : confirmPassword.length > 0 && !passwordsMatch
                          ? "1px solid red"
                          : "1px solid #ccc",
                    boxSizing: "border-box",
                    fontSize: "16px",
                  }}
                />
                {/* Eye icon for confirm password visibility */}
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  {showConfirmPassword ? (
                    <img
                      src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-fill.svg"
                      alt="Hide"
                      style={{ width: "20px", height: "20px" }}
                    />
                  ) : (
                    <img
                      src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-slash-fill.svg"
                      alt="Show"
                      style={{ width: "20px", height: "20px" }}
                    />
                  )}
                </span>
                {/* Check icon when passwords match */}
                {passwordsMatch && confirmPassword.length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      right: "40px",
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <img
                      src={require("../assets/check.png")}
                      alt="valid"
                      style={{ width: "16px", height: "16px" }}
                    />
                  </span>
                )}
              </div>
            </div>

            {/* Save Password Button */}
            <button
              onClick={handleSavePassword}
              style={{
                width: "100%",
                height: "56px",
                padding: "14px",
                borderRadius: "9999px",
                border: "none",
                cursor: canSave ? "pointer" : "not-allowed",
                background: canSave ? "#DE810E" : "rgba(222, 129, 14, 0.4)",
                color: "white",
                fontWeight: "bold",
                fontSize: "1rem",
                transition: "all 0.3s ease",
              }}
              disabled={!canSave}
            >
              Save Password
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatePassword;
